import React from "react";
import { useNavigate } from "react-router-dom";
import { DEPARTMENTBEAUTY, CategoryItem } from "./Categories";

/* "Skincare" -> /shop/skincare, " Body" -> /shop/body, "Hair & Wigs" -> /shop/hair-and-wigs */
const slugOf = (name: string) =>
  name.trim().toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const INK = "#000";
const BODY = "#5C5C5C";
const YEL = "#FFD60A";
const RASP = "#E30B5C";
const LIME = "#C8E17A";
const RED = "#E10000";

/* hover colours take turns: yellow, raspberry, lime, red */
const HV = [
  { c: YEL, f: INK },
  { c: RASP, f: "#fff" },
  { c: LIME, f: INK },
  { c: RED, f: "#fff" },
];
const cycle = HV.map((h, i) => `.ex-item:nth-child(4n+${i + 1}){ --hs:${h.c}; --hf:${h.f}; }`).join("\n");

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Jost:wght@300;400;500;600&display=swap');
.ex-page, .ex-page *, .ex-page *::before, .ex-page *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.ex-page{ min-height:100vh; background:#fff; color:${INK}; overflow-x:hidden;
  padding:calc(28px + env(safe-area-inset-top)) 18px calc(60px + env(safe-area-inset-bottom)); }
.ex-page button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.ex-page button:focus-visible{ outline:3px solid ${INK}; outline-offset:4px; border-radius:24px; }
.ex-wrap{ width:100%; max-width:1100px; margin:0 auto; }

.ex-head{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:10px; }
.ex-head h1{ margin:0; font-family:'Cormorant Garamond',serif; font-size:clamp(40px,8vw,64px); font-weight:600; line-height:1; }
.ex-head p{ margin:8px 0 0; color:${BODY}; font-size:15px; }
.ex-all{ display:inline-flex; align-items:center; height:44px; padding:0 24px; border-radius:999px; border:1px solid ${INK};
  background:linear-gradient(180deg,#fff,#f0f0f0); box-shadow:inset 0 2px 1px #fff, 0 4px 0 ${INK};
  font-size:11.5px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; transition:all .2s; }
.ex-all:hover{ background:${YEL}; transform:translateY(-2px); }
.ex-all:active{ transform:translateY(3px); box-shadow:inset 0 2px 1px #fff, 0 1px 0 ${INK}; }

.ex-grid{ display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:34px 16px; margin-top:34px; justify-items:center; }
@media (min-width:640px){ .ex-grid{ grid-template-columns:repeat(3, minmax(0,1fr)); gap:44px 24px; } }
@media (min-width:980px){ .ex-grid{ grid-template-columns:repeat(4, minmax(0,1fr)); } }

.ex-item{ --hs:${YEL}; --hf:${INK}; width:100%; max-width:230px; display:flex; flex-direction:column; align-items:center; gap:18px; -webkit-tap-highlight-color:transparent; }
${cycle}

/* the sphere */
.ex-sphere{ --c:#0a0a0a; position:relative; isolation:isolate; width:100%; aspect-ratio:1/1; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff;
  font-size:clamp(46px, 11vw, 78px);
  background:radial-gradient(circle at 35% 28%, color-mix(in srgb, var(--c) 58%, #fff) 0, var(--c) 46%, color-mix(in srgb, var(--c) 55%, #000) 100%);
  box-shadow:inset -14px -18px 30px rgba(0,0,0,.5), inset 7px 7px 18px rgba(255,255,255,.32), 0 22px 26px -14px rgba(0,0,0,.55);
  transition:transform .35s cubic-bezier(.2,.9,.3,1.35), box-shadow .3s, background .3s, color .3s; }
.ex-sphere::before{ content:""; position:absolute; z-index:2; top:6%; left:15%; width:52%; height:32%; border-radius:50%; pointer-events:none;
  background:linear-gradient(180deg, rgba(255,255,255,.92), rgba(255,255,255,.04)); }
.ex-sphere::after{ content:""; position:absolute; z-index:2; right:14%; bottom:11%; width:24%; height:12%; border-radius:50%; pointer-events:none;
  background:rgba(255,255,255,.4); transform:rotate(-38deg); filter:blur(1px); }
.ex-sphere svg{ position:relative; z-index:1; filter:drop-shadow(0 4px 4px rgba(0,0,0,.4)); }

.ex-item:hover .ex-sphere, .ex-item:focus-visible .ex-sphere{ --c:var(--hs); color:var(--hf); transform:translateY(-8px) scale(1.05);
  box-shadow:inset -14px -18px 30px rgba(0,0,0,.28), inset 7px 7px 18px rgba(255,255,255,.65), 0 0 0 5px #fff, 0 0 0 8px ${INK}, 0 30px 34px -14px rgba(0,0,0,.55); }
.ex-item:hover .ex-sphere svg{ filter:drop-shadow(0 3px 3px rgba(0,0,0,.25)); }
.ex-item:active .ex-sphere{ transform:scale(.95); }

.ex-label{ padding:6px 18px; border-radius:999px; font-size:12px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; text-align:center; border:1px solid ${INK};
  background:linear-gradient(180deg,#fff,#f1f1f1); box-shadow:inset 0 2px 1px #fff, 0 3px 0 ${INK}; transition:background .2s, color .2s, transform .2s; }
.ex-item:hover .ex-label, .ex-item:focus-visible .ex-label{ background:var(--hs); color:var(--hf); transform:translateY(-2px); }

.ex-empty{ text-align:center; color:${BODY}; padding:60px 0; }

@media (prefers-reduced-motion: reduce){ .ex-page *{ transition:none !important; } }
`;

export default function Explore() {
  const navigate = useNavigate();
  const items: CategoryItem[] = DEPARTMENTBEAUTY;

  return (
    <div className="ex-page">
      <style>{CSS}</style>
      <div className="ex-wrap">
        <div className="ex-head">
          <div>
            <h1>Explore</h1>
            <p>Pick a department to start shopping.</p>
          </div>
          <button type="button" className="ex-all" onClick={() => navigate("/shop")}>All products</button>
        </div>

        {items.length === 0 ? (
          <p className="ex-empty">No departments yet.</p>
        ) : (
          <div className="ex-grid">
            {items.map((d) => (
              <button
                type="button" key={d.id} className="ex-item"
                aria-label={`Shop ${d.name.trim()}`}
                onClick={() => navigate(`/shop/${slugOf(d.name)}`)}
              >
                <span className="ex-sphere">{d.icon}</span>
                <span className="ex-label">{d.name.trim()}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
